import { NavigationGuard, RouteConfig } from 'vue-router';
import { app } from '@src/store/modules/app';
import { growthTechReitId } from '@src/constants';
import { investmentEntity } from '@store/modules/investment-entity';

const investmentsRedirectGuard: NavigationGuard = async (to, from, next) => {
	if (app.isAuthenticated && investmentEntity.isReitInvestor) {
		next({ name: 'account-overview', replace: true });
	} else {
		next({ name: 'investments', replace: true });
	}
};

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const redirectRoutes: Array<RouteConfig> = [
	{
		path: '/account/security/manage',
		name: 'account-security-redirect',
		redirect: { name: 'account-overview' }
	},
	{
		path: '/account/updates',
		name: 'account-updates-redirect',
		redirect: { name: 'newsfeed' }
	},
	{
		path: '/beta',
		alias: '/beta/**',
		name: 'beta-redirect',
		beforeEnter: async (to, from, next) => {
			if (app.isAuthenticated) {
				await next({ name: 'account-overview', replace: true });
			} else {
				await next({ name: 'home', replace: true });
			}
		}
	},
	{
		path: '/portfolio/portfolio-investment/:compositeId/documents',
		name: 'composite-documents-redirect',
		redirect: { name: 'account-documents' }
	},
	{
		path: '/portfolio/portfolio-investment/:compositeId/updates',
		name: 'composite-portfolio-redirect',
		redirect: { name: 'newsfeed' }
	},
	{
		path: '/account/portfolio-investment/:compositeId/transactions',
		name: 'composite-transactions-redirect',
		redirect: { name: 'account-transactions' }
	},
	{
		path: '/portfolio/:offeringId/documents',
		name: 'portfolio-documents-redirect',
		redirect: { name: 'account-documents' }
	},
	{
		path: '/portfolio/:offeringId/transactions',
		name: 'portfolio-transactions-redirect',
		redirect: { name: 'account-transactions' }
	},
	{
		path: '/portfolio/:offeringId/updates',
		name: 'portfolio-updates-redirect',
		redirect: { name: 'newsfeed' }
	},
	{
		path: '/portfolio/reit-investment/:reitId/updates',
		name: 'reit-updates-redirect',
		redirect: { name: 'newsfeed' }
	},
	{
		path: '/portfolio/reit-investment/:reitId/documents',
		name: 'reit-documents-redirect',
		redirect: { name: 'account-documents' }
	},
	{
		path: '/portfolio/reit-investment/:reitId/transactions',
		name: 'reit-transactions-redirect',
		redirect: { name: 'account-transactions' }
	},
	{
		path: '/portfolio/reit-investment/:reitId/activity',
		alias: '/activity/reit-investment/:reitId/your-investment',
		name: 'reit-activity-redirect',
		redirect: (route) => ({
			name: 'account-portfolio-detail',
			params: {
				reitId: route.params.reitId
			}
		})
	},
	{
		path: '/account/investment-accounts/personal-information',
		alias: [
			`/account/investment-accounts`,
			`/account/investment-accounts/personal-information/manage`,
			`/account/investment-accounts/:investmentEntityId/personal-information/manage`
		],
		name: 'settings-account-information-redirect',
		beforeEnter: async (to, from, next) => {
			switch (investmentEntity.entityType) {
				case 'INDIVIDUAL':
					next({ name: 'settings-nickname-view' });
					break;
				case 'IRA':
					next({ name: 'settings-ira-information-view' });
					break;
				case 'JOINT_ACCOUNT':
					next({ name: 'settings-joint-account-information-view' });
					break;
				case 'LLC':
					next({
						name: 'settings-entity-account-information-view'
					});
					break;
				case 'TRUST':
					next({
						name: 'settings-trust-information-view'
					});
					break;
				default:
					next({ name: '404' });
					break;
			}
		}
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/accreditation/edit',
		alias: '/account/investment-accounts/:investmentEntityId/:entityType/accreditation/edit',
		name: 'settings-accreditation-edit-redirect',
		redirect: { name: 'settings-accreditation-edit' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/advanced-options/view',
		alias: '/account/investment-accounts/:investmentEntityId/:entityType/advanced-options/manage',
		name: 'settings-advanced-options-redirect',
		redirect: { name: 'settings-advanced-options' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/recurring-investment-settings/manage',
		alias: '/account/investment-accounts/:investmentEntityId/:entityType/recurring-investment-settings/manage',
		name: 'settings-auto-invest-form-redirect',
		redirect: { name: 'settings-auto-invest-form' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/:entityType/recurring-investment-settings/view',
		name: 'auto-invest-view-redirect',
		redirect: { name: 'auto-invest-view' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId?/:entityType?/payment-methods/manage',
		alias: [
			'/account/investment-accounts/me/individual-account/bank-accounts/manage',
			'/account/investment-accounts/:investmentEntityId?/:entityType?/bank-accounts/manage'
		],
		name: 'settings-payment-methods-view-redirect',
		redirect: { name: 'settings-payment-methods-view' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId?/:entityType?/payment-methods/edit',
		alias: '/account/investment-accounts/:investmentEntityId?/:entityType?/bank-accounts/edit',
		name: 'settings-payment-methods-edit-redirect',
		redirect: { name: 'settings-payment-methods-edit' }
	},
	{
		path: '/account/investment-accounts/:entityType/bank-accounts/new/form',
		alias: '/account/investment-accounts/:investmentEntityId/:entityType/bank-accounts/new/form',
		name: 'settings-bank-accounts-new-redirect',
		redirect: { name: 'settings-bank-accounts-new' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/:entityType/ria-dividend-reinvestment/manage',
		alias: '/account/investment-accounts/:investmentEntityId/:entityType/dividend-reinvestment/manage',
		name: 'settings-drip-edit-entityId-entityType-redirect',
		redirect: { name: 'settings-drip-edit' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/ria-dividend-reinvestment/manage',
		alias: '/account/investment-accounts/:investmentEntityId/dividend-reinvestment/manage',
		name: 'settings-drip-edit-entityId-redirect',
		redirect: { name: 'settings-drip-edit' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/:entityType/ria-dividend-reinvestment/view',
		alias: '/account/investment-accounts/:investmentEntityId/:entityType/dividend-reinvestment/view',
		name: 'settings-drip-view-redirect',
		redirect: { name: 'settings-drip-view' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/:entityType/investment-plan/view',
		alias: '/account/investment-accounts/:investmentEntityId/:entityType/investment-goal/manage',
		name: 'settings-investment-plan-redirect',
		redirect: { name: 'settings-investment-plan-view' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/:entityType/beneficiaries/view',
		alias: '/account/investment-accounts/:investmentEntityId/:entityType/beneficiaries/manage',
		name: 'settings-beneficiaries-view-redirect',
		redirect: { name: 'settings-beneficiaries-view' }
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/beneficiaries/:beneficiaryId/edit`,
		name: 'settings-beneficiary-edit-redirect',
		redirect: (route) => ({
			name: 'settings-beneficiary-edit',
			params: {
				beneficiaryId: route.params.beneficiaryId
			}
		})
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/:entityType/beneficiaries/new/:beneficiaryType',
		name: 'settings-beneficiary-new-redirect',
		redirect: (route) => ({
			name: 'settings-beneficiary-new',
			params: {
				beneficiaryType: route.params.beneficiaryType
			}
		})
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/:entityType/notification-recipients/view',
		alias: '/account/investment-accounts/:investmentEntityId/:entityType/notification-recipients/manage',
		name: 'settings-email-recipients-view-redirect',
		redirect: { name: 'settings-email-recipients-view' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/:entityType/notification-recipients/add',
		name: 'settings-email-recipients-new-redirect',
		redirect: { name: 'settings-email-recipients-new' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/:entityType/notification-recipients/:recipientId/edit',
		name: 'settings-email-recipients-edit-redirect',
		redirect: { name: 'settings-email-recipients-edit' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/:entityType/document-delivery/edit',
		alias: [
			'/account/investment-accounts/:investmentEntityId/:entityType/document-delivery/manage',
			'/account/investment-accounts/:entityType/document-delivery/edit',
			'/account/investment-accounts/:entityType/document-delivery/manage'
		],
		name: 'settings-document-delivery-edit-redirect',
		redirect: { name: 'settings-document-delivery-edit' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/ira/personal-information',
		alias: '/account/investment-accounts/:investmentEntityId/ira/manage',
		name: 'settings-ira-information-view-redirect',
		redirect: { name: 'settings-ira-information-view' }
	},
	{
		path: `/account/investment-accounts/:investmentEntityId/:entityType/investment-plan/edit`,
		alias: `/account/investment-accounts/:investmentEntityId/:entityType/investment-goal/edit`,
		name: 'settings-investment-plan-edit-redirect',
		redirect: { name: 'settings-investment-plan-edit' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/joint-account/account-holder-information/edit',
		name: 'settings-joint-account-information-edit-redirect',
		redirect: { name: 'settings-joint-account-information-edit' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/joint-account/personal-information',
		name: 'settings-joint-account-information-view-redirect',
		redirect: { name: 'settings-joint-account-information-view' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/entity-account/manage',
		alias: '/account/investment-accounts/:investmentEntityId/entity-account/personal-information',
		name: 'settings-manage-entity-account',
		redirect: { name: 'settings-entity-account-information-view' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/entity-account/entity-information/edit',
		name: 'settings-entity-account-information-edit-redirect',
		redirect: { name: 'settings-entity-account-information-edit' }
	},
	{
		path: '/account/investment-accounts/individual-account/manage',
		alias: [
			'/account/investment-accounts/:investmentEntityId/individual-account/manage',
			'/account/email/view',
			'/account/profile/mobile-number/view',
			'/account/security/mobile-number/view',
			'/account/profile/personal-information/edit',
			'/account/investment-accounts/individual-account/personal-information/edit'
		],
		name: 'settings-manage-individual-account',
		redirect: { name: 'profile-personal-information-view' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/joint-account/manage',
		name: 'settings-manage-joint-account',
		redirect: { name: 'settings-joint-account-information-view' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/trust/manage',
		alias: '/account/investment-accounts/:investmentEntityId/trust/personal-information',
		name: 'settings-manage-trust',
		redirect: { name: 'settings-trust-information-view' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/trust/information/edit',
		name: 'settings-trust-information-edit-redirect',
		redirect: { name: 'settings-trust-information-edit' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/individual-account/personal-information/edit',
		name: 'profile-personal-information-edit-redirect',
		redirect: { name: 'profile-address-edit' }
	},
	{
		path: '/account/investment-accounts/:investmentEntityId/individual-account/personal-information',
		name: 'settings-personal-information-view-redirect',
		redirect: { name: 'profile-personal-information-view' }
	},
	{
		path: '/account/upgrade/advanced/ira/direct',
		name: 'account-upgrade-advanced-ira-redirect',
		redirect: { name: 'upgrade-landing' }
	},
	{
		path: '/account/upgrade/basic-plan',
		name: 'account-upgrade-basic-plan-redirect',
		redirect: { name: 'upgrade-landing' }
	},
	{
		path: '/account/upgrade/:serviceLevel/success',
		name: 'account-upgrade-service-level-success-redirect',
		redirect: { name: 'upgrade-success' }
	},
	{
		path: '/account/invest-upgrade/select-starter',
		name: 'account-invest-upgrade-select-starter-redirect',
		redirect: { name: 'add-funds-ria' }
	},
	{
		path: '/account/upgrade/premium-welcome',
		name: 'account-upgrade-premium-welcome-redirect',
		redirect: { name: 'account-overview' }
	},
	{
		path: '/account/upgrade/:serviceLevel',
		name: 'account-upgrade-service-level-redirect',
		redirect: { name: 'upgrade-landing' }
	},
	{
		path: '/innovation-fund',
		redirect: { name: 'view-offering-details', params: { offeringId: growthTechReitId } }
	},
	{
		path: '/innovation',
		redirect: { name: 'view-offering-details', params: { offeringId: growthTechReitId } }
	},
	{
		path: '/account/security/your-devices/manage',
		alias: '/account/security/view-password',
		name: 'profile-security-view-redirect',
		redirect: { name: 'profile-security-view' }
	},
	{
		path: `/investments/starter-plan`,
		beforeEnter: investmentsRedirectGuard
	},
	{
		path: `/start-investing/starter-plan`,
		beforeEnter: investmentsRedirectGuard
	},
	{
		path: `/start-investing/starter-plan/mint`,
		beforeEnter: investmentsRedirectGuard
	},
	{
		path: `/starter-portfolio/details`,
		beforeEnter: investmentsRedirectGuard
	},
	{
		path: `/starter-portfolio/mint`,
		beforeEnter: investmentsRedirectGuard
	},
	{
		path: `/existing/:slug`,
		redirect: { name: 'ria-upgrade' }
	},
	{
		path: '/reits/checkout/ira-profile-form',
		redirect: { name: 'checkout-start' }
	}
];

export default redirectRoutes;
